import React, { useEffect, useState } from "react";
import Layout1 from "../../Layouts/Layout1";
import { Link } from "react-router-dom";
import { usePropertyData } from "../../context/PropertyDataContext";
import { DateFormat } from "../common/utils/getTimeAgo";
import { ArrowRight, Calendar, Clock4 } from "lucide-react";

const SkeletonLoader = () => (
    <div className="flex flex-col rounded-lg bg-white shadow animate-pulse">
        <div className="w-full h-48 bg-gray-300 rounded-t-lg"></div>
        <div className="mb-3 flex w-full flex-col justify-between px-4 pt-2 h-[200px]">
            <div className="flex flex-col justify-between mb-2">
                <div className="h-6 bg-gray-300 w-3/4 rounded-md"></div>
                <div className="h-4 bg-gray-300 w-full mt-2 rounded-md"></div>
                <div className="h-4 bg-gray-300 w-5/6 mt-2 rounded-md"></div>
            </div>
            <div className="h-8 bg-gray-300 w-1/3 mt-auto rounded-md"></div>
        </div>
    </div>
);

const BlogListEmptyState = () => (
    <div className="md:col-span-3 sm:col-span-2 col-span-1">
        <div className="w-full bg-white border border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 shadow-sm min-h-[280px]">
            <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2">
                No blogs available
            </h3>
            <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
                There are no blog posts to show right now. Please check back later.
            </p>
            <Link to="/">
                <button
                    type="button"
                    className="px-5 py-2.5 text-sm font-medium text-white bg-[#e5002a] rounded hover:bg-[#c70024] transition-all duration-300 ease-in-out"
                >
                    Go to Home
                </button>
            </Link>
        </div>
    </div>
);

const Blogs = () => {
    const { fetchBlogs, blogCache } = usePropertyData();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadBlogs = async () => {
            setIsLoading(true);
            await fetchBlogs();
            setIsLoading(false);
        };
        loadBlogs();
    }, []);

    return (
        <Layout1>
            <div className="px-5 pt-6">
                <div className="container mx-auto max-w-6xl">
                    <h2 className="text-3xl font-bold text-center mb-8">Latest Blogs</h2>
                    <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6 pb-4">
                        {isLoading
                            ? Array.from({ length: 3 }).map((_, index) => <SkeletonLoader key={index} />)
                            : blogCache.length === 0
                                ? <BlogListEmptyState />
                                : blogCache.map((blog, index) => (
                                    <Link
                                        to={`/blog/${blog._id}`}
                                        key={blog.id ?? `blog-${index}`}
                                        className="w-full flex-1 group"
                                    >
                                        <div className="hover:shadow-md bg-white border border-[#EAEAEA] rounded-lg w-full">
                                            <div className="overflow-hidden rounded-t-lg">
                                                <img
                                                    src={blog.coverImage}
                                                    alt=""
                                                    className="w-full h-40 sm:h-44 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                                                />
                                            </div>
                                            <div className="p-3 sm:p-4">
                                                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-gray-500 text-xs sm:text-sm">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar size={16} aria-hidden />
                                                        <span>{DateFormat(blog.createdAt)}</span>
                                                    </div>

                                                    <div className="flex items-center gap-1">
                                                        <Clock4 size={16} aria-hidden />
                                                        <span>5 min read</span>
                                                    </div>
                                                </div>

                                                <p className="mt-2 sm:mt-3 text-gray-700 text-sm leading-relaxed min-h-[48px] sm:min-h-[60px]">
                                                    {blog.title}
                                                </p>

                                                <div className="mt-3 sm:mt-4">
                                                    <span className="flex font-medium items-center gap-1 text-[#097FE6] group-hover:gap-2 transition-all text-xs sm:text-sm pointer-events-none">
                                                        Read Article
                                                        <ArrowRight size={14} aria-hidden />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                    </div>
                </div>
            </div>
        </Layout1>
    );
};

export default Blogs;
